import React, { useMemo } from "react";
import { NextPage, NextPageContext } from "next";
import { CommonData } from "../../types/CommonData";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { getCommonData } from "../../helpers/common-data";

import { getAnnouncements } from "../../helpers/getAnnouncements";
import { Announcement } from "../../types/Announcement";
import { notAuthorised } from "../../request";
import { InHome } from "../../types/InHome";
import InHomeView from "../../components/InHomeView";
import { css } from "styled-components";
import { Product } from "types/Product";
import { getRichSnippet } from "helpers/rich-snippets/product";
import { getImageUrl } from "helpers/getImageUrl";
import { getFrontendUrl } from "helpers/utils";
import { SEOTags } from "components/SEOTags";

export const PageInHome: NextPage<{
  commonData: CommonData;
  announcements: Announcement[];
  inHome: InHome | null;
  product: Product | null;
  isPinterestBot: boolean;
}> = (props) => {
  const { inHome, product } = props;

  const richSnippets = useMemo(() => {
    if (!product || !inHome) {
      return undefined;
    }
    const snippet = getRichSnippet(product, false);

    snippet.name = inHome.name || "";
    snippet.description = inHome.description || "";
    snippet.image = [
      getImageUrl(inHome.image),
      getImageUrl(product.featured_image),
    ];
    snippet.url = getFrontendUrl(`/in-home/${inHome.id}`);
    snippet.productId = `${inHome.id}-IH`;

    snippet["@id"] = getFrontendUrl(`/#in-home/${inHome.id}`);

    if (props.isPinterestBot) {
      if (inHome.pinterest_title) {
        snippet.name = inHome.pinterest_title;
      }
      if (inHome.pinterest_description) {
        snippet.description = inHome.pinterest_description;
      }
    }

    return snippet;
  }, [product, inHome, props.isPinterestBot]);

  return (
    <>
      {inHome && product && (
        <SEOTags
          title={inHome.name}
          description={inHome.description}
          type="product"
          keywords={product.keywords}
          url={getFrontendUrl(`/in-home/${inHome.id}`)}
          featuredImage={inHome.image}
          richSnippets={richSnippets}
        />
      )}

      <Header announcements={props.announcements} />
      <div
        css={css`
          position: relative;
          width: 100%;
          height: auto;
        `}
      >
        {inHome && <InHomeView inHome={inHome} />}
      </div>
      <Footer />
    </>
  );
};

interface Context extends NextPageContext {
  query: {
    id: string;
  };
}

PageInHome.getInitialProps = async (ctx: Context) => {
  const commonData = await getCommonData(ctx.req);
  const announcements = await getAnnouncements("IN_HOMES");
  let inHome = null;

  const userAgent = ctx.req?.headers["user-agent"] || "";
  const isPinterestBot = !!userAgent?.toLowerCase().includes("pinterestbot");

  try {
    const response = await notAuthorised.request<InHome>({
      method: "GET",
      url: `/api/frontend/in-homes/${ctx.query.id}/`,
    });

    inHome = response.data;
  } catch (e) {
    inHome = null;
  }

  let product: Product | null = null;

  if (inHome?.products[0]?.product.slug) {
    const productSlug = inHome.products[0].product.slug;

    const response = await notAuthorised.request<Product>({
      method: "GET",
      url: `/api/frontend/products/${productSlug}/`,
    });

    product = response.data;
  }

  return {
    commonData: commonData,
    announcements,
    inHome,
    product,
    isPinterestBot,
  };
};

export default PageInHome;
